import { computed, onBeforeUnmount, onMounted, ref, toRef, toValue } from 'vue'
import { defineStore } from 'pinia'
import { getI18n } from '@/plugins/i18n'

import router from '@/router'
import { chatApi } from '@/apis/chat.api'
import { usePlanningStore } from '@/stores/planning.store'
import { searchResultTypeEnum, useWebSiteStore } from '@/stores/web-site.store'
import { entityChangeTypeEnum, usePortalStore } from '@/stores/portal.store'
import { buildTimeISO } from '@/composables/date-time'
import { readAsDataUrlAsync } from '@/composables/file-upload'
import { toPhotoDataUrlAsync } from '@/composables/image-thumbnail'

export const chatRoleEnum = Object.freeze({
  USER: 0,
  ASSISTANT: 1,
})

export const chatStreamEventEnum = Object.freeze({
  STAGE: 0,
  DRAFT: 1,
  TEXT: 2,
  ERROR: 3,
  DONE: 4,
  TOOL_CALL: 5,
  CONVERSATION: 6,
  CLARIFICATION: 7,
  ACTIONS: 8,
  FORM_LINKS: 9,
  TITLE: 10,
  PENDING_ACTIONS: 11,
  ACTION_RESULTS: 12,
  QUICK_REPLIES: 13,
  TASKS: 14,
})

export const chatPendingActionsDecisionEnum = Object.freeze({
  CONFIRM: 'confirm',
  CANCEL: 'cancel',
})

export const chatStageEnum = Object.freeze({
  RESOLVING: 0,
  ANSWERING: 1,
  ORCHESTRATING: 2,
})

export const chatDraftKindEnum = Object.freeze({
  PROJECT: 0,
  QUOTE: 1,
})

export const chatFormModeEnum = Object.freeze({
  CREATE: 0,
  EDIT: 1,
})

export const CHAT_PDF_MEDIA_TYPE = 'application/pdf'

export const chatActionStatusEnum = Object.freeze({
  PENDING: 0,
  CONFIRMED: 1,
  CANCELLED: 2,
  FAILED: 3,
})

// Mirrors the backend module path of each chat action service: the prefix of `action.action`.
export const chatActionServiceEnum = Object.freeze({
  EXPENSES: 'ai.services.expenses.ChatExpenseService.',
})

const ACTION_ENTITY_CHANGES = Object.freeze({
  'schedule.services.EventService': [entityChangeTypeEnum.EVENT, entityChangeTypeEnum.PROJECT],
  'schedule.services.TaskService': [entityChangeTypeEnum.TASK],
  'planning.services.project.ProjectService': [entityChangeTypeEnum.PROJECT, entityChangeTypeEnum.QUOTE],
  'accounting.services.budget_document.BudgetDocumentService': [entityChangeTypeEnum.QUOTE, entityChangeTypeEnum.PROJECT],
  'planning.services.listing.ListingService': [entityChangeTypeEnum.LISTING],
  'planning.services.equipment.EquipmentService': [entityChangeTypeEnum.EQUIPMENT],
  'planning.services.clone.ServiceCloneService': [entityChangeTypeEnum.STAFF, entityChangeTypeEnum.EQUIPMENT, entityChangeTypeEnum.GROUP],
  'ai.services.expenses.ChatExpenseService': [entityChangeTypeEnum.COST_REPORT_ROW],
})

const actionEntityChanges = (path) => ACTION_ENTITY_CHANGES[path.slice(0, path.lastIndexOf('.'))]

const chatStageLabels = Object.freeze({
  [chatStageEnum.ORCHESTRATING]: 'chat.stageOrchestrating',
  [chatStageEnum.RESOLVING]: 'chat.stageResolving',
  [chatStageEnum.ANSWERING]: 'chat.stageAnswering',
})

const CHAT_FAILED_ERROR = 'error.chatFailed'
const CHAT_BUSY_ERROR = 'error.chatBusy'
const QUICK_REPLY_SEPARATOR = '\n'
const PHOTO_MAX_SIDE = 1600
const PHOTO_MEDIA_TYPE = 'image/jpeg'
const PHOTO_EXTENSION = '.jpg'
const DATA_URL_SEPARATOR = ','
const HTTP_CONFLICT = 409
const ABORT_ERROR_NAME = 'AbortError'
const PROJECT_DETAIL_ROUTE = 'project-detail'
const CHAT_HINT_KEYS = Object.freeze([
  'chat.hint.email',
  'chat.hint.availability',
  'chat.hint.suggest',
  'chat.hint.refine',
  'chat.hint.quote',
  'chat.hint.invoices',
  'chat.hint.assign',
  'chat.hint.cloneListing',
  'chat.hint.create',
  'chat.hint.warehouse',
  'chat.hint.conditions',
  'chat.hint.clientActivity',
  'chat.hint.confirm',
  'chat.hint.page',
  'chat.hint.memory',
  'chat.hint.tasks',
])
const DEFAULT_QUICK_ACTIONS = Object.freeze(['chat.quick.weekPlan', 'chat.quick.freeStaffTomorrow', 'chat.quick.unconfirmedAllocations', 'chat.quick.uninvoiced'])
const WAREHOUSE_QUICK_ACTIONS = Object.freeze(['chat.quick.brokenEquipment', 'chat.quick.weekPlan'])
const QUICK_ACTIONS_BY_ROUTE = Object.freeze({
  'accounting-invoices': ['chat.quick.uninvoiced', 'chat.quick.unpaidInvoices'],
  'accounting-budget-documents': ['chat.quick.uninvoiced', 'chat.quick.unconfirmedAllocations'],
  'staff-movements': ['chat.quick.pendingAbsences', 'chat.quick.freeStaffTomorrow'],
  'warehouse-list': WAREHOUSE_QUICK_ACTIONS,
  'warehouse-stock': WAREHOUSE_QUICK_ACTIONS,
  'warehouse-pivot': WAREHOUSE_QUICK_ACTIONS,
  equipments: WAREHOUSE_QUICK_ACTIONS,
  projects: ['chat.quick.todayTasks', 'chat.quick.overdueTasks', 'chat.quick.weekPlan', 'chat.quick.unconfirmedAllocations'],
})

const newMessage = (fields) => ({
  key: crypto.randomUUID(),
  id: null,
  content: '',
  tool_calls: [],
  draft: null,
  draft_created_id: null,
  clarification: null,
  actions: [],
  form_links: [],
  quick_replies: null,
  task_ids: [],
  files: [],
  error: null,
  tokens: 0,
  stage: null,
  created_at: new Date().toISOString(),
  ...fields,
})

export const useChatStore = defineStore('chat', () => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const webSiteStore = useWebSiteStore()
  const portalStore = usePortalStore()

  const showChat = ref(false)
  const lastConfirmedActions = ref({ paths: [], at: 0 })
  const conversationId = toRef(webSiteStore, 'chatConversationId')
  const conversations = ref([])
  const showHistory = ref(false)
  const showArchivedConversations = ref(false)
  const messages = ref([])
  const isStreaming = ref(false)
  const pageEntities = ref([])
  const formRequest = ref(null)
  let abortController = null

  const quickActionsByEntity = Object.freeze({
    [searchResultTypeEnum.PROJECT]: ['chat.quick.projectSummary', 'chat.quick.projectFreeStaff', 'chat.quick.projectConfirmAllocations', 'chat.quick.projectUninvoiced', 'chat.quick.projectTasks'],
    [searchResultTypeEnum.QUOTE]: ['chat.quick.quoteSummary', 'chat.quick.quoteDuplicate'],
    [searchResultTypeEnum.STAFF]: ['chat.quick.staffSchedule', 'chat.quick.staffAbsences'],
    [searchResultTypeEnum.EQUIPMENT]: ['chat.quick.equipmentAvailability', 'chat.quick.equipmentStatus', 'chat.quick.equipmentDuplicate'],
    [searchResultTypeEnum.GROUP]: ['chat.quick.groupAvailability', 'chat.quick.groupDuplicate'],
    [searchResultTypeEnum.COMPANY]: ['chat.quick.companyActivity', 'chat.quick.companyUninvoiced'],
    [searchResultTypeEnum.RATE_CARD]: ['chat.quick.rateCardIncrease', 'chat.quick.rateCardDuplicate'],
  })

  const isEnabled = computed(() => webSiteStore.can(webSiteStore.permissionFlagEnum.AI_ASSISTANT))

  const conversationTokens = computed(() => messages.value.reduce((total, message) => total + message.tokens, 0))

  const currentConversation = computed(() => conversations.value.find(conversation => conversation.id === conversationId.value) ?? null)

  const routeEntity = () => {
    const route = router.currentRoute.value
    const projectId = route.name === PROJECT_DETAIL_ROUTE ? route.params.id : route.query.projectId
    return projectId ? { type: searchResultTypeEnum.PROJECT, id: Number(projectId) } : null
  }

  const gridRouteEntityTypes = Object.freeze({
    projects: searchResultTypeEnum.PROJECT,
    staffs: searchResultTypeEnum.STAFF,
    equipments: searchResultTypeEnum.EQUIPMENT,
    companies: searchResultTypeEnum.COMPANY,
    groups: searchResultTypeEnum.GROUP,
    listings: searchResultTypeEnum.RATE_CARD,
    'accounting-budget-documents': searchResultTypeEnum.QUOTE,
    'accounting-cost-reports': searchResultTypeEnum.PROJECT,
  })

  const gridEntity = () => {
    const entity = webSiteStore.gridEntity
    const type = gridRouteEntityTypes[router.currentRoute.value.name]
    return entity && type && entity.routeName === router.currentRoute.value.name ? { type, id: entity.id, label: entity.label } : null
  }

  const pageEntity = computed(() => {
    const entity = pageEntities.value.findLast(item => toValue(item.id))
    return entity ? { type: entity.type, id: toValue(entity.id) } : gridEntity() ?? routeEntity()
  })

  const pageContext = computed(() => ({
    route: router.currentRoute.value.fullPath,
    entity_type: pageEntity.value?.type ?? null,
    entity_id: pageEntity.value?.id ?? null,
    entity_label: pageEntity.value?.label ?? null,
  }))

  const quickActions = computed(() => (quickActionsByEntity[pageEntity.value?.type] ??
    QUICK_ACTIONS_BY_ROUTE[router.currentRoute.value.name] ??
    DEFAULT_QUICK_ACTIONS))

  const notifyConfirmedActions = (paths) => {
    const changes = paths.map(actionEntityChanges)
    const types = [...new Set(changes.flat().filter(type => type !== undefined))]
    if (changes.every(Boolean) && portalStore.hasEntityChangeSubscribers(types)) portalStore.notifyEntityChange(types)
    else portalStore.incrementReloadKey()
  }

  const eventHandlers = Object.freeze({
    [chatStreamEventEnum.CONVERSATION]: (answer, event, question) => {
      question.id = event.user_message_id
      if (conversationId.value !== event.conversation_id) {
        conversationId.value = event.conversation_id
        fetchConversationsAsync()
      }
    },
    [chatStreamEventEnum.STAGE]: (answer, event) => { answer.stage = event.stage },
    [chatStreamEventEnum.TOOL_CALL]: (answer, event) => { answer.tool_calls.push(event.tool_call) },
    [chatStreamEventEnum.DRAFT]: (answer, event) => { answer.draft = event.draft },
    [chatStreamEventEnum.CLARIFICATION]: (answer, event) => { answer.clarification = event.clarification },
    [chatStreamEventEnum.ACTIONS]: (answer, event) => { answer.actions = event.actions },
    [chatStreamEventEnum.FORM_LINKS]: (answer, event) => { answer.form_links = event.form_links },
    [chatStreamEventEnum.QUICK_REPLIES]: (answer, event) => { answer.quick_replies = event.quick_replies },
    [chatStreamEventEnum.TASKS]: (answer, event) => { answer.task_ids = event.task_ids },
    [chatStreamEventEnum.PENDING_ACTIONS]: (answer, event) => { answer.pendingActionsDecision = event.decision },
    [chatStreamEventEnum.ACTION_RESULTS]: (answer, event) => {
      const message = messages.value.find(item => item.id === event.message_id)
      if (message) message.actions = event.actions
      const paths = event.actions.filter(action => action.status === chatActionStatusEnum.CONFIRMED).map(action => action.action)
      if (paths.length) {
        lastConfirmedActions.value = { paths, at: Date.now() }
        notifyConfirmedActions(paths)
      }
    },
    [chatStreamEventEnum.TITLE]: (answer, event) => {
      const conversation = conversations.value.find(item => item.id === event.conversation_id)
      if (conversation) conversation.title = event.title
    },
    [chatStreamEventEnum.TEXT]: (answer, event) => { answer.content += event.text },
    [chatStreamEventEnum.ERROR]: (answer, event) => {
      answer.error = event.code
      answer.tokens = event.tokens
    },
    [chatStreamEventEnum.DONE]: (answer, event) => {
      answer.id = event.message_id
      answer.tokens = event.tokens
      answer.stage = null
      if (event.message_id && messages.value.some(message => message !== answer && message.id === event.message_id)) {
        messages.value = messages.value.filter(message => message !== answer)
      }
      if (showChat.value && !showHistory.value && conversationId.value) markConversationReadAsync(conversationId.value)
    },
  })

  const fetchConversationsAsync = async() => {
    const { data } = await chatApi.getAsync(chatApi.conversationsUrl, { show_archived: showArchivedConversations.value })
    conversations.value = data
  }

  const renameConversationAsync = async(conversation, title) => {
    await chatApi.patchAsync(`${chatApi.conversationsUrl}${conversation.id}/`, { title })
    conversation.title = title
  }

  const archiveConversationAsync = async(conversation) => {
    await chatApi.putAsync(`${chatApi.conversationsUrl}${conversation.id}/archive/`)
    if (conversationId.value === conversation.id) newConversation()
    await fetchConversationsAsync()
  }

  const markConversationReadAsync = async(id) => {
    await chatApi.putAsync(`${chatApi.conversationsUrl}${id}/read/`)
    const conversation = conversations.value.find(item => item.id === id)
    if (conversation) conversation.is_unread = false
  }

  const openConversationAsync = async(id, config) => {
    const controller = beginStream()
    // The running turn is opened before the messages are read: once it ends its answer is already saved.
    const runningStream = await chatApi.getRunningStreamAsync(id, controller.signal)
    const { data } = await chatApi.getAsync(`${chatApi.conversationsUrl}${id}/messages/`, undefined, config)
    conversationId.value = id
    messages.value = data.map(message => newMessage(message))
    loadFilePreviewsAsync(messages.value)
    prompt.value = ''
    showHistory.value = false
    markConversationReadAsync(id)
    if (runningStream) {
      const question = messages.value.findLast(message => message.role === chatRoleEnum.USER)
      messages.value.push(newMessage({ role: chatRoleEnum.ASSISTANT }))
      followStreamAsync(controller, runningStream, messages.value.at(-1), question)
    }
  }

  const restoreConversationAsync = async() => {
    if (!conversationId.value || messages.value.length) return
    try {
      await Promise.all([openConversationAsync(conversationId.value, { skipErrorMessage: true }), fetchConversationsAsync()])
    } catch (err) {
      console.error('Failed to restore the last chat conversation', err)
      newConversation()
    }
  }

  const newConversation = () => {
    detachStream()
    prompt.value = ''
    pendingPhoto.value = null
    conversationId.value = null
    messages.value = []
    showHistory.value = false
  }

  const askInNewConversation = (question) => {
    newConversation()
    prompt.value = question
    showChat.value = true
  }

  const deleteConversationAsync = async(id) => {
    await chatApi.deleteAsync(`${chatApi.conversationsUrl}${id}/`)
    if (conversationId.value === id) newConversation()
    await fetchConversationsAsync()
  }

  const markDraftCreatedAsync = async(message, createdId) => {
    message.draft_created_id = createdId
    if (message.id) await chatApi.patchAsync(`${chatApi.messagesUrl}${message.id}/`, { draft_created_id: createdId })
  }

  const prompt = ref('')
  const promptLines = computed(() => prompt.value.split(QUICK_REPLY_SEPARATOR))

  const isQuickReplySelected = (item) => promptLines.value.includes(item.message)

  const toggleQuickReply = (item) => {
    const lines = isQuickReplySelected(item)
      ? promptLines.value.filter(line => line !== item.message)
      : [...promptLines.value, item.message]
    prompt.value = lines.filter(line => line.trim()).join(QUICK_REPLY_SEPARATOR)
  }

  const pendingPhoto = ref(null)

  const toPhotoAsync = async(file) => (file.type === CHAT_PDF_MEDIA_TYPE
    ? { name: file.name, media_type: CHAT_PDF_MEDIA_TYPE, preview: await readAsDataUrlAsync(file) }
    : {
      name: `${file.name.replace(/\.[^.]+$/, '')}${PHOTO_EXTENSION}`,
      media_type: PHOTO_MEDIA_TYPE,
      preview: await toPhotoDataUrlAsync(file, PHOTO_MAX_SIDE),
    })

  const attachPhotoAsync = async(file) => { pendingPhoto.value = await toPhotoAsync(file) }

  const attachmentOf = (photo) => (photo?.preview
    ? { attachment: { name: photo.name, media_type: photo.media_type, data: photo.preview.split(DATA_URL_SEPARATOR)[1] } }
    : {})

  const removePhoto = () => { pendingPhoto.value = null }

  const loadFilePreviewsAsync = (loaded) => Promise.all(
    loaded.flatMap(message => message.files).map(async(file) => { file.preview = await readAsDataUrlAsync(await chatApi.getMessageFileAsync(file.id)) }),
  )

  const latestPhoto = () => {
    const file = messages.value.findLast(message => message.files.some(item => item.preview))?.files.find(item => item.preview)
    return file ? { name: file.name, payload: file.preview } : null
  }

  const streamAsync = async(body, photo = null) => {
    messages.value.push(newMessage({ role: chatRoleEnum.USER, content: body.content, files: photo ? [photo] : [] }))
    const question = messages.value.at(-1)
    messages.value.push(newMessage({ role: chatRoleEnum.ASSISTANT }))
    const answer = messages.value.at(-1)
    const controller = beginStream()
    await followStreamAsync(controller, chatApi.postStreamAsync({ conversation_id: conversationId.value, ...body }, controller.signal), answer, question)
    return answer
  }

  const beginStream = () => {
    detachStream()
    isStreaming.value = false
    abortController = new AbortController()
    return abortController
  }

  const followStreamAsync = async(controller, response, answer, question) => {
    isStreaming.value = true
    try {
      await chatApi.readEventsAsync(await response, event => eventHandlers[event.type]?.(answer, event, question))
    } catch (err) {
      if (err.name !== ABORT_ERROR_NAME) {
        console.error('Failed to stream the chat answer', err)
        answer.error = err.status === HTTP_CONFLICT ? CHAT_BUSY_ERROR : CHAT_FAILED_ERROR
      }
    } finally {
      answer.stage = null
      if (abortController === controller) isStreaming.value = false
    }
  }

  const pendingActionsMessage = () => messages.value.findLast(message => message.actions.some(action => action.status === chatActionStatusEnum.PENDING))

  const applyPendingActionsDecisionAsync = async(decision) => {
    const message = pendingActionsMessage()
    if (!message) return
    await decideActionsAsync(message, decision === chatPendingActionsDecisionEnum.CONFIRM)
  }

  const sendMessageAsync = async(content) => {
    const photo = pendingPhoto.value
    pendingPhoto.value = null
    const answer = await streamAsync({ content: content || i18n.t('chat.receiptDefaultMessage'), context: pageContext.value, ...attachmentOf(photo) }, photo)
    if (answer.pendingActionsDecision) await applyPendingActionsDecisionAsync(answer.pendingActionsDecision)
    return answer
  }

  const rewindAsync = (question, content, photo = question.files[0]) => {
    messages.value = messages.value.slice(0, messages.value.indexOf(question))
    return streamAsync({ content, context: pageContext.value, ...(question.id ? { replace_from_message_id: question.id } : {}), ...attachmentOf(photo) }, photo?.preview ? photo : null)
  }

  const questionOf = (message) => (message.role === chatRoleEnum.USER
    ? message
    : messages.value.slice(0, messages.value.indexOf(message)).findLast(item => item.role === chatRoleEnum.USER))

  const canRewind = (message) => !isStreaming.value && !!questionOf(message)

  const regenerateAsync = (message) => {
    const question = questionOf(message)
    return rewindAsync(question, question.content)
  }

  const toggleActionIncluded = (action) => { action.excluded = !action.excluded }

  const pendingActions = (message) => message.actions.filter(action => action.status === chatActionStatusEnum.PENDING)

  const decideActionsAsync = async(message, confirmed) => {
    const pending = pendingActions(message)
    for (const action of pending) {
      action.decision = confirmed && !action.excluded ? chatActionStatusEnum.CONFIRMED : chatActionStatusEnum.CANCELLED
    }
    const anyConfirmed = pending.some(item => item.decision === chatActionStatusEnum.CONFIRMED)
    await streamAsync({
      content: i18n.t(anyConfirmed ? 'chat.actionConfirmed' : 'chat.actionCancelled'),
      action_decisions: {
        message_id: message.id,
        decisions: pending.map(({ tool_call_id, decision }) => ({ tool_call_id, status: decision })),
      },
    })
  }

  const draftItem = (draft) => {
    const startTime = draft.daily_start_time ?? webSiteStore.getDefaultStartTime()
    const endTime = draft.daily_end_time ?? webSiteStore.getDefaultEndTime()
    return {
      name: draft.name,
      description: draft.description,
      company: draft.company_id ?? draft.company_name,
      contact: draft.contact_id ?? draft.contact_name,
      listing: draft.listing_id,
      schedules: [{ start_time: startTime, end_time: endTime }],
      ...(draft.start_date && {
        start: buildTimeISO(draft.start_date, startTime),
        end: buildTimeISO(draft.end_date ?? draft.start_date, endTime),
      }),
    }
  }

  const draftContactReferences = (draft) => (draft.contact_id ? null : {
    [planningStore.referenceTypeEnum.PHONE]: draft.contact_phone ?? '',
    [planningStore.referenceTypeEnum.EMAIL]: draft.contact_email ?? '',
  })

  const getDraftForm = (message) => ({
    message,
    kind: message.draft.kind,
    item: draftItem(message.draft),
    serviceIds: message.draft.kind === chatDraftKindEnum.PROJECT ? message.draft.services.map(service => service.id) : [],
    contactReferences: draftContactReferences(message.draft),
  })

  const openForm = (form) => { formRequest.value = form }

  // Called from a component's setup: the hooks bind to that component.
  const trackPageEntity = (type, id) => {
    const entity = { type, id }
    onMounted(() => { pageEntities.value = [...pageEntities.value, entity] })
    onBeforeUnmount(() => { pageEntities.value = pageEntities.value.filter(item => item !== entity) })
  }

  const detachStream = () => abortController?.abort()

  const stopStreamingAsync = async() => {
    if (conversationId.value) {
      await chatApi.deleteAsync(`${chatApi.streamUrl}${conversationId.value}/`)
    } else {
      detachStream()
    }
  }

  const getStageLabel = (stage) => chatStageLabels[stage]

  return {
    isEnabled,
    showChat,
    lastConfirmedActions,
    conversationId,
    conversations,
    showHistory,
    showArchivedConversations,
    currentConversation,
    conversationTokens,
    messages,
    isStreaming,
    quickActions,
    hintKeys: CHAT_HINT_KEYS,
    formRequest,
    fetchConversationsAsync,
    openConversationAsync,
    restoreConversationAsync,
    newConversation,
    askInNewConversation,
    deleteConversationAsync,
    renameConversationAsync,
    archiveConversationAsync,
    markDraftCreatedAsync,
    sendMessageAsync,
    canRewind,
    regenerateAsync,
    editMessageAsync: rewindAsync,
    pendingActions,
    toggleActionIncluded,
    decideActionsAsync,
    getDraftForm,
    openForm,
    trackPageEntity,
    stopStreamingAsync,
    prompt,
    isQuickReplySelected,
    toggleQuickReply,
    pendingPhoto,
    attachPhotoAsync,
    toPhotoAsync,
    removePhoto,
    latestPhoto,
    getStageLabel,
  }
})
